/**
 * Add a debt / trust account (D03 §2 flow 1; old `handleAddDebt` → `proceedWithAddDebt` App.tsx:19134-19603):
 * the name is checked first (`GET /customers/check-name`, D-017: exact case-insensitive match, archived accounts
 * included, D-072) and the old same-name prompt opens on a match — with the new options "open the other kind for the
 * same person" (D-017) and "restore" (D-072); the silent " (n)" suffix of a prefix match (R-8) is gone. Then
 * `POST /customers` with the PIN of a `create_customers` holder (own or a supervisor's, D-018): the account and its
 * opening entry through the box (debt = cash out, trust = cash in; D-016 (d), D-218 (b)) in one transaction.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AddCustomerModal, DuplicateNameModal, type AddCustomerValues, type DuplicateNameMatch } from "@faylasuf/ui";
import type { CreateCustomerBody, CustomerNameCheck } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { errorMessage, isSessionLost } from "../../app/errors.js";
import { usePinGate } from "../../app/pin-gate.js";
import { useToast } from "../../app/toast.js";
import { officeDate } from "../wallets/StatementContainer.js";
import { selectOptions, type CustomerBoxes } from "./boxes.js";
import { kindLabel, positive } from "./format.js";

function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function AddContainer({ boxes, usdHidden, kindDefault, kindLocked, themeColor, onClose, onViewStatement, onRestore }: {
  boxes: CustomerBoxes;
  usdHidden: boolean;
  kindDefault: "debt" | "trust";
  kindLocked: boolean;
  themeColor: string;
  onClose: () => void;
  onViewStatement: (accountId: string) => void;
  onRestore: (accountId: string, name: string) => void;
}) {
  const { t, source, now } = useApp();
  const { session, sessionLost } = useSession();
  const toast = useToast();
  const pin = usePinGate();
  const qc = useQueryClient();
  const [values, setValues] = useState<AddCustomerValues>({
    name: "", accountNumber: "", phone: "", address: "", currency: "IQD", amount: "", kind: kindDefault, boxId: boxes.iqdVaultId, note: "", dueDate: "",
  });
  const [dup, setDup] = useState<CustomerNameCheck["matches"] | null>(null);
  const [busy, setBusy] = useState(false);

  const fail = (e: unknown) => {
    if (isSessionLost(e)) sessionLost();
    else toast(errorMessage(t, e), "error");
  };

  const create = async (personId?: string) => {
    const amount = positive(values.amount) ? values.amount : "0";
    const body: CreateCustomerBody = {
      ...(personId ? { personId } : { name: values.name.trim() }),
      kind: values.kind,
      ...(values.phone.trim() ? { phone: values.phone.trim() } : {}),
      ...(values.address.trim() ? { address: values.address.trim() } : {}),
      ...(values.accountNumber.trim() ? { accountNumber: values.accountNumber.trim() } : {}),
      ...(values.dueDate ? { dueDate: values.dueDate } : {}),
      currency: values.currency,
      amount,
      ...(amount !== "0" ? { boxAccountId: values.boxId } : {}),
      ...(values.note.trim() ? { note: values.note.trim() } : {}),
      idempotencyKey: crypto.randomUUID(),
    };
    setBusy(true);
    try {
      const res = await pin.withPin((proof) => source.createCustomer(body, proof), { message: t("cust.pin_create", { name: values.name.trim() }), allowSupervisor: true });
      if (!res) return;
      toast(res.pendingId ? t("toast.sent_for_approval") : t("cust.created"), res.pendingId ? "info" : "success");
      for (const k of [["customers"], ["accounts"], ["dashboard"], ["approvals"]]) void qc.invalidateQueries({ queryKey: k });
      onClose();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    const name = values.name.trim();
    if (!name) return;
    if (positive(values.amount) && !values.boxId) {
      toast(t("cust.select_box_first"), "error");
      return;
    }
    try {
      const check = await source.customerNameCheck(name);
      if (check.matches.length > 0) {
        setDup(check.matches);
        return;
      }
    } catch (e) {
      fail(e);
      return;
    }
    await create();
  };

  const matches: DuplicateNameMatch[] = (dup ?? []).map((m) => ({ id: m.id, name: m.name, kindLabel: kindLabel(t, m.kind), accountNumber: m.accountNumber, archived: m.archived }));
  const canOpenFor = (id: string): boolean => {
    const m = dup?.find((x) => x.id === id);
    return Boolean(m && !m.archived && m.kind !== values.kind && !(dup ?? []).some((x) => x.personId === m.personId && x.kind === values.kind));
  };

  return (
    <>
      {!dup && (
        <AddCustomerModal
          values={values}
          onChange={(p) =>
            setValues((v) => ({ ...v, ...p, ...(p.currency && p.currency !== v.currency ? { boxId: p.currency === "USD" ? boxes.dollarVaultId : boxes.iqdVaultId } : {}) }))
          }
          kindLocked={kindLocked}
          boxOptions={selectOptions(values.currency === "USD" ? boxes.usd : boxes.iqd, t)}
          usdHidden={usdHidden}
          themeColor={themeColor}
          busy={busy}
          onDueShortcut={(days) => setValues((v) => ({ ...v, dueDate: addDays(officeDate(now(), session.office.timeZone), days) }))}
          onSubmit={() => void submit()}
          onClose={onClose}
        />
      )}
      {dup && (
        <DuplicateNameModal
          name={values.name.trim()}
          matches={matches.length > 0 ? matches : []}
          onViewStatement={(id) => onViewStatement(id)}
          {...(matches.some((m) => canOpenFor(m.id))
            ? {
                onOpenForSamePerson: (id: string) => {
                  const m = dup.find((x) => x.id === id);
                  if (m && canOpenFor(id)) {
                    setDup(null);
                    void create(m.personId);
                  } else toast(t("cust.same_person_exists"), "error");
                },
              }
            : {})}
          {...(matches.some((m) => m.archived) ? { onRestore: (id: string) => onRestore(id, values.name.trim()) } : {})}
          onChangeName={() => setDup(null)}
          onCancel={() => setDup(null)}
        />
      )}
      {pin.dialog}
    </>
  );
}
